/**
 * Account — profile, membership, legal, delete.
 *
 * Own stack (sibling of home), opened via push from drawer / account menu.
 * Back via header (`resolveHeaderBack`). Sign out lives in the account menu /
 * drawer, not here.
 */
import { HelpScoutBeacon, openHelpScoutBeacon } from '@/components/HelpScoutBeacon';
import {
  SettingsHero,
  SettingsPanel,
  SettingsRow,
} from '@/components/settings/SettingsPanel';
import Container from '@/components/ui/Container';
import { CaptionText, PageTitle } from '@/components/ui/Typography';
import { useUserSubscriptionState } from '@/hooks/useUserSubscriptionState';
import { useAuth } from '@/lib/AuthContext';
import { BRAND_COLORS as C } from '@/lib/brandColors';
import { URLS } from '@/lib/constants';
import {
  playbackPreferenceLabel,
  setPlaybackPreference,
  usePlaybackPreference,
  type PlaybackPreference,
} from '@/lib/offline';
import { CONTENT_MAX_WIDTH, useIsMobile } from '@/lib/useDeviceType';
import { resolveUserDisplayName } from '@/lib/userDisplayName';
import { useRouter } from 'expo-router';
import React from 'react';
import { Alert, Linking, Platform, Pressable, ScrollView } from 'react-native';
import { useMedia, XStack, YStack } from 'tamagui';

function tierLabel(tier: string): string {
  if (tier === 'pro') return 'Open Studio Pro';
  if (tier === 'regular') return 'Open Studio';
  return 'No membership';
}

function fmtDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export default function SettingsScreen() {
  const router = useRouter();
  const isMobile = useIsMobile();
  const media = useMedia();
  const sideBySide = media.gtSm;
  const { user, membershipTier } = useAuth();
  const { data: subscription } = useUserSubscriptionState({ userId: user?.id });
  const playbackPreference = usePlaybackPreference();

  const name = resolveUserDisplayName(user?.user_metadata) || null;
  const plan = tierLabel(membershipTier);
  const isMember = membershipTier !== 'non_member';

  const renewal = fmtDate(subscription?.next_payment_at);
  const accessEnds = fmtDate(subscription?.access_ends_at);
  const planHint = subscription?.is_lifetime
    ? 'Lifetime access'
    : subscription?.cancelled_at && accessEnds
      ? `Access until ${accessEnds}`
      : renewal
        ? `Renews ${renewal}`
        : null;

  const sectionGap = isMobile ? '$3' : '$4';

  // iOS keeps the plain external-link glyph; elsewhere rows that edit the
  // account show a pencil and read-only pages show an eye.
  const isIOS = Platform.OS === 'ios';
  const editIcon = isIOS ? undefined : 'pencil-outline';
  const viewIcon = isIOS ? undefined : 'eye-outline';
  const isWeb = Platform.OS === 'web';

  // Web opens the HelpScout beacon on its message form (same as WP /contact/);
  // native has no beacon, so it keeps the support email.
  const contactSupport = () => {
    if (!openHelpScoutBeacon('/ask')) void Linking.openURL(URLS.SUPPORT_EMAIL);
  };

  const membershipPanel = (
    <SettingsPanel title="Membership">
      <SettingsRow
        label="Current plan"
        value={plan}
        hint={planHint}
        external={isWeb}
        onPress={() =>
          isWeb ? void Linking.openURL(URLS.MY_ACCOUNT) : router.push('/membership')
        }
      />
      <SettingsRow
        label="Orders and billing"
        hint="Invoices, payment method and addresses"
        external
        icon={viewIcon}
        onPress={() => void Linking.openURL(URLS.ORDERS)}
      />
    </SettingsPanel>
  );

  const choosePlaybackPreference = () => {
    const options: PlaybackPreference[] = ['ask', 'download', 'stream'];
    Alert.alert(
      'Downloaded videos',
      'When you open a downloaded video while online:',
      [
        ...options.map((value) => ({
          text: playbackPreferenceLabel(value),
          onPress: () => void setPlaybackPreference(value),
        })),
        // Android alerts hold three buttons at most; tapping outside cancels there.
        ...(Platform.OS === 'ios' ? [{ text: 'Cancel', style: 'cancel' as const }] : []),
      ],
      { cancelable: true },
    );
  };

  // Native only: offline downloads live on the device, so the entry point
  // sits with the account (web has no downloads).
  const downloadsPanel = !isWeb ? (
    <SettingsPanel title="Offline">
      <SettingsRow
        label="My downloads"
        hint="Videos saved on this device"
        icon="download-outline"
        onPress={() => router.push('/downloads' as never)}
      />
      <SettingsRow
        label="Downloaded videos"
        hint="When you open one while online"
        value={playbackPreferenceLabel(playbackPreference ?? 'ask')}
        onPress={choosePlaybackPreference}
      />
    </SettingsPanel>
  ) : null;

  const legalPanel = (
    <SettingsPanel title="Legal and support">
      <SettingsRow
        label="Terms of Service"
        external
        icon={viewIcon}
        onPress={() => void Linking.openURL(URLS.TERMS)}
      />
      <SettingsRow
        label="Privacy Policy"
        external
        icon={viewIcon}
        onPress={() => void Linking.openURL(URLS.PRIVACY)}
      />
      <SettingsRow
        label="Contact support"
        external={!isWeb}
        icon={isWeb ? 'chatbubble-ellipses-outline' : viewIcon}
        onPress={contactSupport}
      />
    </SettingsPanel>
  );

  return (
    <YStack f={1} bg={C.pageBg}>
      <ScrollView
        contentContainerStyle={{ paddingBottom: isMobile ? 40 : 56 }}
        showsVerticalScrollIndicator={false}
      >
        <Container
          maxWidth={CONTENT_MAX_WIDTH}
          centered
          py={isMobile ? '$4' : '$6'}
        >
          <PageTitle color={C.text} mb={sectionGap} ta="left">
            Account
          </PageTitle>

          <YStack gap={sectionGap}>
            <SettingsHero
              name={name}
              email={user?.email}
              badge={isMember ? plan : null}
              onEditName={() => router.push('/settings/profile')}
            />

            <SettingsPanel title="Sign-in">
              <SettingsRow
                label="Password"
                hint="Reset using your email"
                external
                icon={editIcon}
                onPress={() => void Linking.openURL(URLS.LOST_PASSWORD)}
              />
              <SettingsRow
                label="Manage"
                hint="Full website account"
                external
                icon={editIcon}
                onPress={() => void Linking.openURL(URLS.MY_ACCOUNT)}
              />
            </SettingsPanel>

            {downloadsPanel}

            {sideBySide ? (
              <XStack gap={sectionGap} ai="stretch">
                <YStack f={1} miw={0}>
                  {membershipPanel}
                </YStack>
                <YStack f={1} miw={0}>
                  {legalPanel}
                </YStack>
              </XStack>
            ) : (
              <>
                {membershipPanel}
                {legalPanel}
              </>
            )}

            {/* In-app deletion exists to satisfy Apple 5.1.1(v); on web,
                account closure stays a support request as before. */}
            {Platform.OS !== 'web' ? (
              <YStack ai="center" pt="$2" pb="$1">
                <Pressable
                  onPress={() => router.push('/settings/delete-account')}
                  accessibilityRole="button"
                  hitSlop={12}
                >
                  <CaptionText color={C.muted} ta="center">
                    Delete account
                  </CaptionText>
                </Pressable>
              </YStack>
            ) : null}
          </YStack>
        </Container>
      </ScrollView>
      <HelpScoutBeacon />
    </YStack>
  );
}
